import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { Boton } from '#components/mobile/ui/Boton';
import { color, texto } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';

import { CONFIG_VACIA } from './nomina';
import type { ConfigNomina, NominaDeducida } from './nomina';
import { nombreMes } from './textos';

type PropuestaNominaProps = {
  deducida: NominaDeducida;
  /** «Sí, es así»: se guarda y se pone al día sola. */
  onConfirmar: (config: ConfigNomina) => void;
  /** «Corregir»: se guarda tal cual para cambiarla a mano. */
  onCorregir: (config: ConfigNomina) => void;
};

/**
 * Lo que Rumbo ha deducido de los ingresos («Nómina de 1.850 € a final de
 * mes y pagas extra en junio y diciembre. ¿Es así?»), para confirmarlo con un
 * toque sin escribir números.
 */
export function PropuestaNomina({
  deducida,
  onConfirmar,
  onCorregir,
}: PropuestaNominaProps) {
  const { t, i18n } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const { pagador: _pagador, ...datos } = deducida;
  const config: ConfigNomina = { ...CONFIG_VACIA, ...datos };

  const amount = format(deducida.neta, 'financial');
  const day =
    deducida.dia === 'ultimo'
      ? t('at the end of the month')
      : t('around day {{day}}', { day: deducida.dia });
  const months = new Intl.ListFormat(i18n.language, {
    type: 'conjunction',
  }).format(deducida.pagas.map(p => nombreMes(p.mes, locale)));

  return (
    <View
      data-testid="nomina-propuesta"
      style={{
        gap: 10,
        padding: 14,
        borderRadius: 16,
        backgroundColor: color.okSoft,
      }}
    >
      <Text style={{ ...texto.secundario, color: color.fg }}>
        <PrivacyFilter>
          {deducida.pagas.length > 0
            ? t(
                'From your income, Rumbo sees pay of {{amount}} {{day}} and extra payments in {{months}}. Is that right?',
                { amount, day, months },
              )
            : t(
                'From your income, Rumbo sees pay of {{amount}} {{day}}, with no extra payments. Is that right?',
                { amount, day },
              )}
        </PrivacyFilter>
      </Text>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <Boton
          data-testid="nomina-propuesta-si"
          onPress={() => onConfirmar({ ...config, auto: true })}
        >
          <Trans>Yes, that is right</Trans>
        </Boton>
        <Boton
          variante="fantasma"
          data-testid="nomina-propuesta-corregir"
          onPress={() => onCorregir({ ...config, auto: false })}
        >
          <Trans>Correct it</Trans>
        </Boton>
      </View>
    </View>
  );
}
